import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { log } from '@/lib/logger'

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: () => import('@/views/HomeView.vue') },
  {
    path: '/stock', name: 'stock', component: () => import('@/views/StockView.vue'),
    meta: { title: 'Stock, batteries and parts in transit' },
  },
  {
    path: '/stock/:id', name: 'part', component: () => import('@/views/PartView.vue'), props: true,
    meta: { title: 'Stock item' },
  },
  {
    path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'),
    meta: { title: 'Sign in' },
  },
  {
    path: '/admin/:tab?',
    name: 'admin',
    component: () => import('@/views/AdminView.vue'),
    props: true,
    meta: { requiresAdmin: true, title: 'Dashboard' },
  },
  {
    path: '/admin/reservations/:id',
    name: 'reservation',
    component: () => import('@/views/AdminView.vue'),
    props: (route) => ({ tab: 'queue', focus: route.params.id }),
    meta: { requiresAdmin: true, title: 'Reservation' },
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

/**
 * Vue Router keeps the scroll offset in history state, which survives a reload —
 * so refreshing halfway down the page put you back halfway down the page. The
 * first navigation after a load always goes to the top; back and forward within
 * the session still restore where you were, which is what those buttons mean.
 */
let firstNavigation = true

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (to, _from, saved) => {
    if (firstNavigation) { firstNavigation = false; return { top: 0 } }
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return saved ?? { top: 0 }
  },
})

const SITE = 'ElectroCell Solutions'

/**
 * Only the home page was setting a title, so every other page sat under the
 * home page's one — which is what a browser tab, a bookmark and a shared link
 * all show.
 */
router.afterEach((to) => {
  const page = to.meta.title as string | undefined
  document.title = page ? `${page} · ${SITE}` : SITE
})

/**
 * The guard is a convenience, not the security boundary. Firestore rules are
 * what actually stop an unverified session writing — a guard in the browser can
 * always be stepped around.
 */
router.beforeEach(async (to) => {
  if (!to.meta.requiresAdmin) return true

  const auth = useAuthStore()
  // Wait out the first token read rather than bouncing on a cold load.
  for (let i = 0; auth.stage === 'loading' && i < 50; i++) {
    await new Promise((r) => setTimeout(r, 40))
  }

  if (!auth.canAdminister) {
    log.info('admin route blocked', { to: to.fullPath, stage: auth.stage })
    return { name: 'login', query: { next: to.fullPath } }
  }
  return true
})
